import { FC, useEffect, useSyncExternalStore } from "react";
import { resolveBrand } from "@appcaire/brand";
import { useWebsiteBrand } from "@/lib/brandContext";
import { Helmet } from "react-helmet-async";
import { getOrganizationData, getWebsiteSchema } from "./organization-data";
import { useTranslation } from "react-i18next";
import { BASE_URL } from "@/config/env";
import { useLocation } from "react-router-dom";

export const DefaultSeo: FC = () => {
  const brandPreview = useWebsiteBrand();
  const brandId = useSyncExternalStore(
    brandPreview.subscribe,
    brandPreview.getSnapshot,
    brandPreview.getServerSnapshot,
  );
  const brand = resolveBrand(brandId);
  const { t, i18n } = useTranslation(["common", "home"]);
  const location = useLocation();

  // Detect language from URL path
  useEffect(() => {
    const pathLang = location.pathname.startsWith("/en") ? "en" : "sv";
    if (i18n.language !== pathLang) {
      i18n.changeLanguage(pathLang);
    }
  }, [location.pathname, i18n]);

  // Get translated data
  const organizationData = getOrganizationData();
  const websiteData = getWebsiteSchema();

  // Determine current language and locale
  const currentLang = i18n.language || "sv";
  const locale = currentLang === "en" ? "en-US" : "sv-SE";

  // Self-referencing canonical, kept as a fallback for routes that render raw
  // Helmet without PageSeo (e.g. the legacy Features/* pages). For pages that DO
  // render PageSeo this matches its canonical and Helmet de-dupes it.
  //
  // hreflang is deliberately NOT emitted here: DefaultSeo can only guess
  // alternates by adding/stripping `/en`, which is wrong whenever the sv/en
  // slugs differ (/om-oss vs /en/about). Because Helmet only de-dupes identical
  // <link>s, that wrong guess shipped a second, conflicting hreflang set next to
  // the correct page-level one. PageSeo owns hreflang per page.
  const canonicalUrl = `${BASE_URL}${location.pathname}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang={currentLang} />
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <title>
        {t("home:seo.default.title")} | {t("home:seo.site")}
      </title>
      <meta name="description" content={t("home:seo.default.description")} />

      {/* No site-wide AI-crawler meta tags — they are not directives any of
       * those vendors read, and public/robots.txt already lets every crawler,
       * AI crawlers included, read the public site through its single
       * `User-agent: *` group. The `suppressIndexingDirectives` path that gated
       * them for the two investor pages went with them: those pages are
       * noindex through their own PageSeo, which is what actually withholds
       * them. Full reasoning in PageSeo.tsx. */}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta
        property="og:title"
        content={`${t("home:seo.default.title")} | ${t("home:seo.site")}`}
      />
      <meta
        property="og:description"
        content={t("home:seo.default.description")}
      />
      <meta property="og:image" content={`${BASE_URL}/images/og/default.jpg`} />
      <meta property="og:locale" content={locale} />
      {currentLang === "en" && (
        <meta property="og:locale:alternate" content="sv_SE" />
      )}
      {currentLang === "sv" && (
        <meta property="og:locale:alternate" content="en_US" />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta
        name="twitter:title"
        content={`${t("home:seo.default.title")} | ${t("home:seo.site")}`}
      />
      <meta
        name="twitter:description"
        content={t("home:seo.default.description")}
      />
      <meta
        name="twitter:image"
        content={`${BASE_URL}/images/og/default.jpg`}
      />

      {/* Canonical — self-referencing fallback (see note above). */}
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang intentionally omitted — PageSeo owns it per page (see note). */}

      {/* Performance Optimization: Preconnect and Prefetch */}
      <link rel="preconnect" href={`${BASE_URL}`} />
      <link rel="dns-prefetch" href={`${BASE_URL}`} />

      {/* Web App Manifest */}
      <link rel="manifest" href="/manifest.json?v=favicon-v2" />

      {/* Favicons */}
      <link rel="icon" href="/favicons/favicon-caire-v2.ico" />
      <link
        rel="icon"
        href={brand.assets.websiteIcon}
        type="image/svg+xml"
        sizes="any"
      />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/favicons/favicon-caire-v2-180x180.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/favicons/favicon-caire-v2-32x32.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/favicons/favicon-caire-v2-16x16.png"
      />
      <meta name="msapplication-TileColor" content={brand.colors.action} />
      <meta
        name="msapplication-TileImage"
        content="/favicons/favicon-caire-v2-32x32.png"
      />
      <meta name="theme-color" content={brand.colors.action} />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationData)}
      </script>
      <script type="application/ld+json">{JSON.stringify(websiteData)}</script>
    </Helmet>
  );
};
